import { useState } from 'react';
import { usePartnerPayouts } from '../hooks/usePartnerPayouts';
import { PayoutModal } from '../components/PayoutModal';
import { Button, Card, CardContent, Badge } from '@medikei/ui';
import { Spinner } from '@medikei/ui';
import { Wallet, Filter, Clock, RefreshCw, AlertCircle, ShieldCheck } from 'lucide-react';
import { usePartnerBalance } from '../hooks/usePartnerDashboard';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Payout } from '../types/partner';

const statusFilters = [
  { value: '', label: 'Todos' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'PROCESSING', label: 'Processando' },
  { value: 'COMPLETED', label: 'Concluído' },
  { value: 'FAILED', label: 'Falhou' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

const statusConfig: Record<string, { variant: 'warning' | 'info' | 'success' | 'danger'; label: string }> = {
  PENDING: { variant: 'warning', label: 'Pendente' },
  PROCESSING: { variant: 'info', label: 'Processando' },
  COMPLETED: { variant: 'success', label: 'Concluído' },
  FAILED: { variant: 'danger', label: 'Falhou' },
  CANCELLED: { variant: 'danger', label: 'Cancelado' },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function PayoutsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const limit = 10;

  const { data, isLoading, error, refetch } = usePartnerPayouts({
    page,
    limit,
    status: statusFilter || undefined,
  });

  const { data: balance } = usePartnerBalance();

  const payouts = data?.items || [];
  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;
  const availableBalance = balance?.available ?? 0;
  const pendingBalance = balance?.pending ?? 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com Ação de Saque */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
            Saques
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Gerencie e acompanhe suas solicitações de resgate via PIX
          </p>
        </div>
        <Button
          onClick={() => setPayoutModalOpen(true)}
          className="gap-2 w-full sm:w-auto shadow-md shadow-primary/20 rounded-xl font-bold h-11"
        >
          <Wallet className="w-4 h-4" />
          Solicitar Saque
        </Button>
      </div>

      {/* Cards de Resumo Financeiro */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-4 sm:p-5 shadow-xs">
          <div className="flex items-center justify-between text-xs font-semibold text-primary mb-1">
            <span className="flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" />
              Saldo Disponível
            </span>
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25">
              PIX
            </span>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-foreground tracking-tight mt-1">
            {formatCurrency(availableBalance)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Saque mínimo: R$ 50,00
          </p>
        </Card>

        <Card className="rounded-2xl border border-border p-4 sm:p-5 shadow-xs bg-card">
          <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground mb-1">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-500" />
              Comissões Pendentes
            </span>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-foreground tracking-tight mt-1">
            {formatCurrency(pendingBalance)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Liberadas após confirmação da consulta
          </p>
        </Card>

        <Card className="rounded-2xl border border-border p-4 sm:p-5 shadow-xs bg-card flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground mb-1">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                Segurança e Prazos
              </span>
            </div>
            <p className="text-xs font-semibold text-foreground mt-1">
              Transferências instantâneas
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Pagamentos via Chave PIX cadastrada no perfil
            </p>
          </div>
        </Card>
      </div>

      {/* Tabela de Saques e Filtros */}
      <Card className="rounded-2xl border border-border overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mr-1">
                <Filter className="w-3.5 h-3.5" />
                Status:
              </span>
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => {
                    setStatusFilter(filter.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer ${
                    statusFilter === filter.value
                      ? 'bg-primary/10 text-primary border border-primary/25 shadow-xs'
                      : 'bg-muted/40 text-muted-foreground border border-transparent hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner size="lg" />
              <p className="text-xs text-muted-foreground">Carregando histórico de saques...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border rounded-xl p-6">
              <AlertCircle className="w-10 h-10 text-destructive/80 mb-2" />
              <p className="text-sm font-bold text-foreground">Não foi possível carregar os saques</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mb-4">
                Houve uma instabilidade momentânea na sincronização com o serviço financeiro.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="gap-2 rounded-xl font-semibold"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Tentar novamente
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-border bg-muted/20 sm:bg-transparent">
                    <th className="text-left py-3 px-3 sm:px-4 text-xs font-semibold text-muted-foreground">
                      Data
                    </th>
                    <th className="text-left py-3 px-3 sm:px-4 text-xs font-semibold text-muted-foreground">
                      Valor
                    </th>
                    <th className="text-left py-3 px-3 sm:px-4 text-xs font-semibold text-muted-foreground">
                      Método
                    </th>
                    <th className="text-center py-3 px-3 sm:px-4 text-xs font-semibold text-muted-foreground">
                      Status
                    </th>
                    <th className="text-left py-3 px-3 sm:px-4 text-xs font-semibold text-muted-foreground">
                      Chave / Destino
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-muted-foreground">
                        <Wallet className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-sm font-semibold text-foreground">Nenhum saque solicitado ainda</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Quando você solicitar o resgate de suas comissões, o progresso aparecerá aqui.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    payouts.map((payout: Payout) => {
                      const cfg = statusConfig[payout.status] || { variant: 'warning' as const, label: payout.status };

                      return (
                        <tr
                          key={payout.id}
                          className="border-b border-border/60 hover:bg-muted/40 transition-colors"
                        >
                          <td className="py-3.5 px-3 sm:px-4 text-xs sm:text-sm text-foreground whitespace-nowrap">
                            {format(new Date(payout.requestedAt), "dd MMM yyyy", {
                              locale: ptBR,
                            })}
                          </td>
                          <td className="py-3.5 px-3 sm:px-4 text-xs sm:text-sm font-extrabold text-foreground whitespace-nowrap">
                            {formatCurrency(payout.amount)}
                          </td>
                          <td className="py-3.5 px-3 sm:px-4 text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                            {payout.paymentMethod === 'PIX' ? (
                              <span className="font-semibold text-primary">PIX</span>
                            ) : (
                              'Transferência'
                            )}
                          </td>
                          <td className="py-3.5 px-3 sm:px-4 text-center whitespace-nowrap">
                            <Badge variant={cfg.variant} className="font-bold text-[11px]">
                              {cfg.label}
                            </Badge>
                          </td>
                          <td className="py-3.5 px-3 sm:px-4 text-xs text-muted-foreground max-w-[200px] truncate">
                            {payout.pixKey && `PIX: ${payout.pixKey}`}
                            {payout.bankAccount && `${payout.bankAccount.bankName}`}
                            {!payout.pixKey && !payout.bankAccount && '--'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {data && data.total > limit && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-border/60">
              <p className="text-xs text-muted-foreground">
                Mostrando {(page - 1) * limit + 1} a {Math.min(page * limit, data.total)} de{' '}
                {data.total} saques
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-xl font-medium"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-xl font-medium"
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PayoutModal
        open={payoutModalOpen}
        onOpenChange={setPayoutModalOpen}
        availableBalance={balance?.available || 0}
        pendingBalance={balance?.pending}
      />
    </div>
  );
}